import { ReactElement } from 'react';
import { To } from 'react-router-dom';
import { twJoin, twMerge } from 'tailwind-merge';

import { Tooltip } from '@dbt-labs/sourdough';

import { AnnotationBadge } from '@/components/AnnotationBadge';
import { NavButton } from './NavButton';

export type OptionConfig<TOption extends string = string> = {
  option: TOption;
  icon: ReactElement;
  label?: string;
  count?: number | undefined;
  isBeta?: boolean;
};

export interface NavButtonGroupParams<TOption extends string> {
  options: OptionConfig<TOption>[];
  currentOption: TOption | undefined;
  getTargetUrl: (option: TOption) => string;
}

export interface NavButtonOptionParams<TOption extends string> {
  to: To;
  currentOption?: TOption | undefined;
  config: OptionConfig<TOption>;
  index?: number;
  className?: string;
  isBeta?: boolean;
}

const transitionDelays = [
  'delay-0',
  'delay-[25ms]',
  'delay-[50ms]',
  'delay-75',
  'delay-100',
  'delay-[125ms]',
  'delay-150',
  'delay-[175ms]',
  'delay-200',
  'delay-[225ms]',
  'delay-[250ms]',
  'delay-[275ms]',
  'delay-300',
];
export const NavButtonOption = <TOption extends string>({
  to,
  currentOption,
  config,
  index = 0,
  className = '',
  isBeta = false,
}: NavButtonOptionParams<TOption>) => {
  const countClassname = twJoin(
    'flex-0 py-1 transition-opacity',
    transitionDelays[index % transitionDelays.length],
    config.count === undefined ? 'opacity-0' : 'opacity-100',
  );
  const displayText = config.label || config.option;

  return (
    <NavButton
      className={twMerge(
        'flex h-9 w-full cursor-pointer items-center space-x-2 rounded-lg p-2 text-left align-middle',
        currentOption !== config.option && 'text-fgDecorative',
        currentOption === config.option && 'text-fgMain',
        className,
      )}
      id={config.option}
      to={to}
      type="none"
      isSelected={currentOption === config.option}
      key={config.option}
    >
      <span className="flex-0 m-auto w-4">{config.icon}</span>
      <span className="flex-1 overflow-hidden">
        <Tooltip
          displayOnlyWhenTruncated
          content={displayText}
          childrenAreInteractive={true}
        >
          {(ref) => (
            <>
              <span ref={ref} className="block truncate">
                {displayText}
                {isBeta && <AnnotationBadge type="text" text="Beta" />}
              </span>
            </>
          )}
        </Tooltip>
      </span>
      <span className={countClassname}>{config.count}</span>
    </NavButton>
  );
};

export function NavButtonGroup<TOption extends string>({
  options,
  currentOption,
  getTargetUrl,
}: NavButtonGroupParams<TOption>) {
  return (
    <div className="p-1">
      <div
        className={twJoin(
          options.length ? 'opacity-100' : 'opacity-0',
          'duration-300 motion-reduce:duration-0',
        )}
      >
        {options.map((config, idx) => (
          <NavButtonOption
            key={config.option}
            config={config}
            to={getTargetUrl(config.option)}
            currentOption={currentOption}
            index={idx}
            isBeta={config.isBeta}
          />
        ))}
      </div>
    </div>
  );
}
